import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { useFeatures } from '../../../context/FeatureContext';
import { useCapabilities } from '../../../context/CapabilityContext';
import { AUTO_BOOKING_EXPLANATION, autoBookingPayload, autoBookingStatus, canManageAutoBooking, formatCap } from './autoBookingSettings';

/**
 * "Automatisk bokföring av leverantörsfakturor" (automation-by-entitlement SPEC
 * D9): the card on the periodisering settings page; its logic is
 * autoBookingSettings.js (the two names differ only in case and extension,
 * hence "Card"). It holds the cap only, for administrators with the auto_post
 * and periodisering entitlements; whether the policy runs is the entitlement
 * and the kill switch on the Automatisering page. Everything the policy refuses
 * waits for approval as before; the explanation says so in one sentence.
 */
export default function AutoBookingCard({ companyId }) {
  const { features } = useFeatures();
  const { capabilities } = useCapabilities();
  const visible = canManageAutoBooking({ features, isAdmin: capabilities?.isAdmin });
  const [data, setData] = useState(null); const [capText, setCapText] = useState('');
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false); const [scheduleNote, setScheduleNote] = useState(false);
  const url = getApiUrl(`${companyId}/periodisering/auto-booking`);
  useEffect(() => {
    if (!visible) return;
    axios.get(url, { withCredentials: true })
      .then((r) => { setData(r.data); setCapText(formatCap(r.data.amountCap)); })
      .catch(() => setError('Kunde inte hämta inställningarna.'));
  }, [url, visible]);
  if (!visible) return null;

  async function save() {
    const { payload, error: capError } = autoBookingPayload({ capText });
    if (capError) { setError(capError); return; }
    setBusy(true); setError('');
    try {
      const r = await axios.put(url, payload, { withCredentials: true });
      setData(r.data); setCapText(formatCap(r.data.amountCap));
      setScheduleNote(r.data.autoAnswerScheduleSynced === false);
    } catch (e) { setError(e.response?.data?.error || 'Kunde inte spara.'); } finally { setBusy(false); }
  }

  return <div className="modern-card p-4 mt-3"><h4>Automatisk bokföring av leverantörsfakturor</h4><p>{AUTO_BOOKING_EXPLANATION}</p>
    {error && <Alert variant="danger">{error}</Alert>}{!data ? <Spinner size="sm" animation="border" /> : <>
      <Form.Group className="my-3" controlId="auto-booking-cap"><Form.Label>Beloppsgräns (kr inkl. moms)</Form.Label>
        <Form.Control type="text" inputMode="decimal" value={capText} placeholder={formatCap(data.defaultCap)} aria-describedby="auto-booking-cap-help" onChange={(e) => setCapText(e.target.value)} />
        <Form.Text id="auto-booking-cap-help" muted>Tomt betyder standardgränsen {formatCap(data.defaultCap)} kr.</Form.Text>
      </Form.Group>
      <p>
        {autoBookingStatus(data)}
        {!data.active && <> <Link to={`/${companyId}/settings/automation`}>Till Automatisering</Link></>}
      </p>
      {scheduleNote && <Alert variant="info">Schemat uppdateras inom kort.</Alert>}
      <Button onClick={save} disabled={busy}>{busy ? 'Sparar…' : 'Spara'}</Button>
    </>}
  </div>;
}
